import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, Trash2, Loader2, ChevronDown } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { Label } from '@/components/shadcn/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { ColorPicker } from '@/components/common/ColorPicker'
import { cn } from '@/lib/utils'
import {ICON_BY_NAME, iconForName} from "@/components/common/iconByName.tsx";

const ICON_ENTRIES = Object.entries(ICON_BY_NAME)

/**
 * Symbol selection (shared: action modules + service modules). Two mutually
 * exclusive modes (as in the old portal): either an uploaded image
 * OR an icon + color. When an image is set, icon/color selection is hidden;
 * it becomes available again only after removing the image. iconName/colorHex are
 * preserved in the process (the backend only deletes the IconUrl), so the icon
 * comes back unchanged after removal.
 */
export function IconColorPicker({
                                  iconName,
                                  colorHex,
                                  iconUrl,
                                  busy,
                                  uploadDisabled = false,
                                  onIconChange,
                                  onColorChange,
                                  onUpload,
                                  onDelete,
                                }: {
  iconName: string | null
  colorHex: string
  iconUrl: string | null
  busy: boolean
  /** Hide the image upload (e.g. on create, while the object has no Id yet). */
  uploadDisabled?: boolean
  onIconChange: (name: string) => void
  onColorChange: (hex: string) => void
  onUpload: (file: File, dataUrl: string) => void
  onDelete: () => void
}) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [iconPickerOpen, setIconPickerOpen] = useState(false)

  const PreviewIcon = iconForName(iconName)

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      const dataUrl = evt.target?.result as string
      onUpload(file, dataUrl)
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-2">
      <Label>{t('iconColorPicker.icon')}</Label>
      <div className="flex items-start gap-3">
        {/* Vorschau */}
        <div className="flex size-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
          {iconUrl ? (
            <img src={iconUrl} alt="" className="size-full object-cover" />
          ) : (
            <PreviewIcon className="size-8" style={colorHex ? { color: colorHex } : undefined} />
          )}
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />

        {iconUrl ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="text-destructive hover:text-destructive"
                onClick={onDelete}
                disabled={busy}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('iconColorPicker.removeImage')}</TooltipContent>
          </Tooltip>
        ) : uploadDisabled ? null : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('iconColorPicker.uploadImage')}</TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Icon + color only selectable without an image */}
      {!iconUrl && (
        <>
          {/* Icon selection in the popover: the grid is only rendered on open, so
              color changes don't re-draw ~40 icons (lag on the color picker). */}
          {/* modal: otherwise the surrounding dialog's react-remove-scroll blocks
              the mouse wheel in the (portaled-out) popover content. */}
          <Popover modal open={iconPickerOpen} onOpenChange={setIconPickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="mt-1 w-fit gap-2">
                <PreviewIcon className="size-4" style={colorHex ? { color: colorHex } : undefined} />
                {t('iconColorPicker.chooseIcon')}
                <ChevronDown className="size-3.5 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="flex max-h-48 flex-wrap gap-1 overflow-y-auto scrollbar-custom">
                {ICON_ENTRIES.map(([name, Icon]) => (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    onClick={() => { onIconChange(name); setIconPickerOpen(false) }}
                    className={cn(
                      'flex size-8 items-center justify-center rounded-md border transition-colors',
                      iconName === name ? 'border-foreground bg-muted' : 'border-transparent hover:bg-muted',
                    )}
                  >
                    <Icon className="size-4" style={{ color: colorHex }} />
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <ColorPicker value={colorHex} onChange={onColorChange} className="mt-1" />
        </>
      )}
    </div>
  )
}
